import { inject, injectable } from 'inversify';
import Debug from 'debug';
import * as Api from '@xlearn/common';
import { MsgPackCourseFileLoader, MsgPackCourseFileLoaderSymbol } from '@/app/fetch/msgpack-course-file';
import { CancelToken } from '@/app/shared/utils';
import { QuestionStoreModuleSymbol, QuestionStoreModule, Question, QuestionLevel } from '../../store';
import { replaceBasePathInContent,
         walkObjectAndReplaceBasePaths } from '@/app/shared/utils/base-path';

const debug = Debug('load-questions-command');

export interface QuestionSource {
  uri: string;
  basePath: string;
}

@injectable()
export class LoadQuestionsCommandHandler {

  constructor(
    @inject(QuestionStoreModuleSymbol)
    private store: QuestionStoreModule,
    @inject(MsgPackCourseFileLoaderSymbol)
    private fileLoader: MsgPackCourseFileLoader) {
  }

  public async execute(source: QuestionSource, cancelToken: CancelToken) {

    debug('loading questions file', source);

    this.store.startLoading();

    const loadResult = await this.fileLoader.load<Api.SerializedQuestion[]>(source.uri, cancelToken)
    if (!loadResult.success) {
      return loadResult
    }

    const mapped = loadResult.value.map((x) => this.mapQuestion(x, source.basePath));

    debug('question file loaded: %j', mapped);

    this.store.finishLoading(mapped);

    return Api.ok(true as true)
  }

  private mapQuestion(question: Api.SerializedQuestion, basePath: string): Question {
    debug('mapping question', question)

    const replacePath = replaceBasePathInContent(basePath)

    const mapped: Question = {
      id: question.id,
      type: question.type,
      question: replacePath(question.question),
      content: walkObjectAndReplaceBasePaths(question.content, basePath), // TODO: might be done per question
      contentScore: {
        min: question.contentScore.min,
        max: question.contentScore.max
      },
      feedback: {
        correct: replacePath(question.feedback.correct),
        incorrect: replacePath(question.feedback.incorrect),
        tryAgain: replacePath(question.feedback.tryAgain)
      },
      minScore: question.minScore,
      maxScore: question.maxScore,
      level: mapLevel(question.level),
      topicIds: question.topicIds,
      competenceIds: question.competenceIds
    };

    debug('mapped question', mapped)

    function mapLevel(level: Api.QuestionLevel): QuestionLevel {
      switch (level) {
        case Api.QuestionLevel.easy:
          return 'easy'
        case Api.QuestionLevel.normal:
          return 'normal'
        case Api.QuestionLevel.hard:
          return 'hard'
      }
    }
    
    return mapped
  }
}
